import React, { Fragment, useContext } from 'react';
import { useRecoilValue } from 'recoil';
import { isReportState, type StudioRequestSubject } from 'venn-state';
import { isNil, uniq } from 'lodash';
import { FootnoteSymbols, hasFeesExcluded } from 'venn-utils';
import {
  blockSubjectFooterStates,
  pageFootNoteDetails,
  renderedPageFootNoteDetails,
  type BlockSubjectFooterState,
} from '../../../../../venn-state/src/report-lab/footNotesAndSuperscripts';
import { useBlockId } from '../../../studio-blocks/contexts/BlockIdContext';
import moment from 'moment';
import { StudioPrintSettingsContext } from '../../../contexts/studio-print-settings-context';
import { default as UserContext } from '../../../contexts/user-context';

const formatDate = (date?: number | null) => (!isNil(date) ? moment.utc(date).format('DD MMM YYYY') : undefined);

export const renderSuperscripts = (items: { condition: boolean; symbol: string | undefined }[]): React.ReactNode => {
  const validItems = items.filter((item) => item.condition && item.symbol);
  if (validItems.length === 0) return null;

  return (
    <sup>
      {validItems.map((item, index) => (
        <Fragment key={item.symbol}>
          {index > 0 && ', '}
          {item.symbol}
        </Fragment>
      ))}
    </sup>
  );
};

export const useGetBlockSubjectSuperscripts = () => {
  const blockId = useBlockId();
  const footers = useRecoilValue(blockSubjectFooterStates(blockId));

  const getBlockSubjectSuperscripts = ({
    subject,
    isBenchmark,
  }: {
    subject: StudioRequestSubject;
    isBenchmark: boolean;
  }) => {
    const areFeesExcluded = hasFeesExcluded(subject);
    return uniq(
      footers
        .filter(
          (a) =>
            a.blockId === blockId &&
            a.subject.id === subject.id &&
            a.isBenchmark === isBenchmark &&
            (a.type !== 'excluded-fees' || areFeesExcluded) &&
            a.showSuperscript,
        )
        .map((s) => s.symbol),
    ).join(',');
  };

  return getBlockSubjectSuperscripts;
};

export const useBlockSubjectSuperscripts = ({
  subject,
  isBenchmark,
}: {
  subject: StudioRequestSubject;
  isBenchmark: boolean;
}) => {
  const getBlockSubjectSuperscripts = useGetBlockSubjectSuperscripts();
  return getBlockSubjectSuperscripts({ subject, isBenchmark });
};

export const useHasVennModificationsSymbol = () => {
  const blockId = useBlockId();
  const isReport = useRecoilValue(isReportState);
  const footers = useRecoilValue(blockSubjectFooterStates(blockId));
  return (
    (isReport ? footers.find((note) => note.type === 'venn-modified')?.symbol : undefined) ??
    FootnoteSymbols.vennModifiedData
  );
};

export const usePageCustomMetricSymbol = () => {
  const blockId = useBlockId();
  const isReport = useRecoilValue(isReportState);
  const footers = useRecoilValue(blockSubjectFooterStates(blockId));
  return (
    (isReport ? footers.find((note) => note.type === 'custom-metrics-or-metadata')?.symbol : undefined) ??
    FootnoteSymbols.customMetrics
  );
};

export const usePageProxySymbol = () => {
  const blockId = useBlockId();
  const isReport = useRecoilValue(isReportState);
  const footers = useRecoilValue(blockSubjectFooterStates(blockId));
  return (isReport ? footers.find((note) => note.type === 'proxy')?.symbol : undefined) ?? FootnoteSymbols.proxyUsage;
};

export const usePageVdparSymbol = () => {
  const blockId = useBlockId();
  const isReport = useRecoilValue(isReportState);
  const footers = useRecoilValue(blockSubjectFooterStates(blockId));
  return isReport ? footers.find((note) => note.type === 'venn-daily-private-asset')?.symbol : undefined;
};

const formatFootNotes = (
  footnotes: BlockSubjectFooterState[],
  organizationDisplayName: string | undefined,
): React.ReactNode[] => {
  const dateFootNotes = footnotes.filter((note) => note.type === 'date');
  return [
    dateFootNotes?.[0] && (
      <div>
        <sup>{dateFootNotes?.[0].symbol}</sup> Portfolio Allocations are as of
        {` ${formatDate(dateFootNotes[0].allocationsAsOfDate)}`}
        {dateFootNotes.slice(1).map((note, index) => (
          <span>
            {getSeparator(index + 1, dateFootNotes.length)}
            <sup>{note.symbol}</sup> {formatDate(note.allocationsAsOfDate)}
          </span>
        ))}
      </div>
    ),
    ...footnotes
      .filter((note) => note.type !== 'date')
      .map((note) => (
        <div key={note.symbol}>
          <sup>{note.symbol}</sup> {note.message(organizationDisplayName)}
        </div>
      )),
  ];
};

const useOrganizationDisplayName = () => {
  const { profileSettings } = useContext(UserContext);
  const { organizationPrintSettings } = useContext(StudioPrintSettingsContext);
  return organizationPrintSettings?.organizationName ?? profileSettings?.organization?.name;
};

export const usePageFootNotesList = (pageNumber: number) => {
  const footnotes = useRecoilValue(pageFootNoteDetails(pageNumber));
  return formatFootNotes(footnotes, useOrganizationDisplayName());
};

export const useRenderedPageFootNotesList = (renderedIndex: number) => {
  const footnotes = useRecoilValue(renderedPageFootNoteDetails(renderedIndex));
  return formatFootNotes(footnotes, useOrganizationDisplayName());
};

const getSeparator = (i: number, len: number) => (i >= len - 1 ? ', and ' : ', ');
